import React, { useEffect, useRef, useState } from 'react';
import {
  FlatList,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  type DimensionValue,
} from 'react-native';

import { Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { useSearchProfiles } from '@sunrise-ui/api-client';
import { type Profile } from '@sunrise-ui/api/profile';
import {
  Colors,
  Loader,
  TextInput,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { TextInput as PaperTextInput } from 'react-native-paper';
import { ProfileResult } from '../profiles/ProfileResult';

type Props = {
  onSelect: (profile: Profile) => void | Promise<void>;
  width?: DimensionValue;
  maxHeight?: number;
  hiddenIds?: string[];
  placeholderLabel?: string;
  minChars?: number;
  profileResultSize?: 'small' | 'medium';
  profileResultOneLineText?: boolean;
  closeOnOutsideClick?: boolean;
};

export function PersonSearch({
  onSelect,
  width,
  maxHeight,
  hiddenIds,
  placeholderLabel = 'Search for someone...',
  minChars = 3,
  profileResultSize = 'medium',
  profileResultOneLineText = false,
  closeOnOutsideClick = true,
}: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showResults, setShowResults] = useState(true);
  const containerRef = useRef<View>(null);
  const shouldShowResults = Boolean(search && showResults);
  const styles = useDynamicStyles(createStyles, {
    shouldShowResults,
    width,
    maxHeight,
  });
  const isDebouncing = search !== debouncedSearch;

  // Debounce logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Show results when user starts typing
  useEffect(() => {
    if (search) {
      setShowResults(true);
    }
  }, [search]);

  // Outside click detection - only on web platform
  useEffect(() => {
    if (!closeOnOutsideClick || Platform.OS !== 'web') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current) {
        const container = containerRef.current as unknown as Element;
        if (container && !container.contains?.(event.target as Node)) {
          setShowResults(false);
        }
      }
    };

    // Only add listener on web
    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [closeOnOutsideClick]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isRefetching,
  } = useSearchProfiles(debouncedSearch);

  const profiles = data?.pages.flatMap((page) => page?.results ?? []);

  const results = profiles?.filter((profile) =>
    hiddenIds?.length ? !hiddenIds.includes(profile.user_id) : true,
  );

  const hasRelevantData =
    data &&
    data?.pages[0]?.searchQuery.slice(0, minChars) ===
      search.slice(0, minChars);

  return (
    <View ref={containerRef} style={styles.container}>
      <TextInput
        label={placeholderLabel}
        showLabel={false}
        value={search}
        onChange={setSearch}
        onFocus={() => setShowResults(true)}
        style={styles.searchInput}
        autoCorrect={false}
        left={
          <PaperTextInput.Icon
            icon={() => (
              <HugeiconsIcon icon={Search01Icon} color={Colors.text} />
            )}
            loading={
              hasRelevantData &&
              (search.length > minChars || debouncedSearch.length > minChars) &&
              (isDebouncing || isRefetching)
            }
          />
        }
      />
      {shouldShowResults && (
        <FlatList
          data={hasRelevantData ? results : []}
          bounces={false}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          renderItem={({ item: profile }) => (
            <TouchableOpacity
              key={profile.user_id}
              style={styles.listItem}
              onPress={async () => {
                await onSelect(profile);
                setShowResults(false); // Close dropdown after selection
              }}
            >
              <ProfileResult
                userId={profile.user_id}
                size={profileResultSize}
                isOneLineText={profileResultOneLineText}
              />
            </TouchableOpacity>
          )}
          keyExtractor={(item) => item.user_id}
          onEndReached={async () => {
            if (hasNextPage && !isFetching) {
              await fetchNextPage();
            }
          }}
          windowSize={4}
          initialNumToRender={5}
          ListFooterComponent={isFetchingNextPage ? <Loader /> : null}
          contentContainerStyle={styles.listContent}
          style={styles.list}
          ListEmptyComponent={
            search ? (
              <View style={styles.emptyListItem}>
                {search.length > minChars - 1 ? (
                  hasRelevantData ? (
                    <TextReg>No results found</TextReg>
                  ) : (
                    <Loader />
                  )
                ) : (
                  <TextReg>
                    {`Please enter ${minChars} or more characters to search`}
                  </TextReg>
                )}
              </View>
            ) : null
          }
        />
      )}
    </View>
  );
}

const createStyles = ({
  isMobile,
  isTablet,
  maxHeight,
  shouldShowResults,
}: WithResponsive<Props & { shouldShowResults: boolean }>) => {
  const borderRadius = 24;
  return StyleSheet.create({
    container: {
      flexGrow: 1,
      flexShrink: 1,
      maxHeight,
    },
    searchInput: {
      backgroundColor: Colors.opacity05,
      borderTopRightRadius: borderRadius,
      borderTopLeftRadius: borderRadius,
      borderBottomRightRadius: shouldShowResults ? 0 : borderRadius,
      borderBottomLeftRadius: shouldShowResults ? 0 : borderRadius,
    },
    list: {
      borderBottomRightRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
    },
    listContent: {
      backgroundColor: Colors.opacity05,
      paddingHorizontal: isMobile ? 12 : 24,
      borderBottomRightRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
    },
    listItem: {
      display: 'flex',
      flexDirection: 'row',
      gap: 20,
      alignItems: 'center',
      marginBottom: 20,
    },
    emptyListItem: {
      paddingVertical: isMobile ? 12 : isTablet ? 16 : 20,
    },
  });
};
